import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import ZStudioNav from '@/pages/dashboard/apps/zstudio/components/ZStudioNav';
import {
  listZCampaigns,
  createZCampaign,
  updateZCampaign,
  deleteZCampaign,
  type ZStudioCampaign,
} from '@/lib/zstudio';

const STATUS_META: Record<string, { label: string; className: string }> = {
  draft: { label: 'Brouillon', className: 'bg-background-100 text-foreground-600' },
  active: { label: 'Active', className: 'bg-accent-100 text-accent-700' },
  archived: { label: 'Archivée', className: 'bg-secondary-100 text-secondary-800' },
};

export default function ZStudioCampaignsPage() {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState<ZStudioCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try { setCampaigns(await listZCampaigns(user.id)); }
    catch (e) { setError(e instanceof Error ? e.message : 'Erreur de chargement'); }
    finally { setLoading(false); }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const duplicate = async (c: ZStudioCampaign) => {
    if (!user?.id) return;
    try {
      await createZCampaign({ ownerId: user.id, projectId: c.project_id, name: `${c.name || 'Campagne'} (copie)`, subject: c.subject || undefined, objective: c.objective || undefined, season: c.season || undefined, platforms: c.platforms || [], status: 'draft', content: c.content || undefined });
      load();
    } catch { /* ignore */ }
  };

  const archive = async (c: ZStudioCampaign) => {
    setCampaigns((prev) => prev.map((x) => (x.id === c.id ? { ...x, status: 'archived' } : x)));
    try { await updateZCampaign(c.id, { status: 'archived' }); } catch { load(); }
  };

  const remove = async (c: ZStudioCampaign) => {
    if (!window.confirm('Supprimer cette campagne ?')) return;
    setCampaigns((prev) => prev.filter((x) => x.id !== c.id));
    try { await deleteZCampaign(c.id); } catch { load(); }
  };

  if (!user?.id) return <div className="flex items-center justify-center py-24"><i className="ri-loader-4-line animate-spin text-2xl text-accent-500"></i></div>;

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-rocket-line text-accent-500"></i>Campagnes
          </h2>
          <p className="text-sm text-foreground-500 mt-1">Vos campagnes complètes : concept, slogan, textes, hashtags, médias et calendrier (spec 69 & 30).</p>
        </div>
        <Link to="/dashboard/zstudio/wizard?type=campaign&mode=campaign" className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-add-line"></i>Nouvelle campagne
        </Link>
      </div>
      <ZStudioNav />

      {error && <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg"><i className="ri-error-warning-line"></i>{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-2xl text-accent-500"></i></div>
      ) : campaigns.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-background-50 border border-dashed border-background-300/70 rounded-xl text-center">
          <span className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3"><i className="ri-rocket-line text-2xl text-foreground-400"></i></span>
          <p className="text-sm font-medium text-foreground-700">Aucune campagne pour l’instant</p>
          <p className="text-xs text-foreground-500 mt-1">Lancez une « Campagne complète » depuis Créer.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {campaigns.map((c) => {
            const meta = STATUS_META[c.status] || STATUS_META.draft;
            const content = (c.content || {}) as Record<string, unknown>;
            return (
              <div key={c.id} className="bg-background-50 border border-background-200/70 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <span className="w-11 h-11 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0"><i className="ri-rocket-line text-lg"></i></span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-foreground-900 truncate">{c.name || c.subject || 'Campagne'}</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${meta.className}`}>{meta.label}</span>
                  </div>
                  <p className="text-[11px] text-foreground-500 truncate">
                    {[c.objective, c.season, (c.platforms || []).join(', ')].filter(Boolean).join(' · ') || '—'}
                  </p>
                  {typeof content.slogan === 'string' && content.slogan ? <p className="text-[11px] text-foreground-600 italic truncate mt-0.5">« {content.slogan} »</p> : null}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Link to="/dashboard/zstudio/creations" className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-eye-line"></i>Ouvrir</Link>
                  <button type="button" onClick={() => duplicate(c)} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer" title="Dupliquer"><i className="ri-file-copy-line"></i></button>
                  {c.status !== 'archived' && (
                    <button type="button" onClick={() => archive(c)} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-secondary-800 transition-colors cursor-pointer" title="Archiver"><i className="ri-archive-line"></i></button>
                  )}
                  <button type="button" onClick={() => remove(c)} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 transition-colors cursor-pointer" title="Supprimer"><i className="ri-delete-bin-line"></i></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}